import React, { createContext, useContext, useState, useCallback } from 'react';

export interface SquadMember {
  id: string;
  name: string;
  alias: string;
  email: string;
  user_id: string;
  avatar?: string;
  brand_id: string;
  type: 'Growth Partner' | 'Client' | 'Third Party';
  slack_id?: string;
  origin_brand?: string;
  created_at: string;
  updated_at: string;
  description?: string;
}

export type DialogSource = 'growth-squad' | 'growth-lab-team' | 'default';

interface SquadMemberDialogContextType {
  isOpen: boolean;
  mode: 'create' | 'edit';
  editingMember: SquadMember | null;
  source: DialogSource;
  openCreateDialog: (source?: DialogSource) => void;
  openEditDialog: (member: SquadMember, source?: DialogSource) => void;
  closeDialog: () => void;
  onSuccess?: () => void;
  setOnSuccess: (callback: (() => void) | undefined) => void;
}

const SquadMemberDialogContext = createContext<SquadMemberDialogContextType | undefined>(undefined);

export const SquadMemberDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [editingMember, setEditingMember] = useState<SquadMember | null>(null);
  const [onSuccess, setOnSuccessCallback] = useState<(() => void) | undefined>(undefined);
  const [source, setSource] = useState<DialogSource>('default');

  const openCreateDialog = useCallback((dialogSource: DialogSource = 'default') => {
    setMode('create');
    setEditingMember(null);
    setSource(dialogSource);
    setIsOpen(true);
  }, []);

  const openEditDialog = useCallback((member: SquadMember, dialogSource: DialogSource = 'default') => {
    setMode('edit');
    setEditingMember(member);
    setSource(dialogSource);
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setEditingMember(null);
  }, []);

  const setOnSuccess = useCallback((callback: (() => void) | undefined) => {
    setOnSuccessCallback(() => callback);
  }, []);

  return (
    <SquadMemberDialogContext.Provider
      value={{
        isOpen,
        mode,
        editingMember,
        source,
        openCreateDialog,
        openEditDialog,
        closeDialog,
        onSuccess,
        setOnSuccess,
      }}
    >
      {children}
    </SquadMemberDialogContext.Provider>
  );
};

export const useSquadMemberDialog = () => {
  const context = useContext(SquadMemberDialogContext);
  if (!context) {
    throw new Error('useSquadMemberDialog must be used within SquadMemberDialogProvider');
  }
  return context;
};
